import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Button,
  Typography,
  Space,
  List,
  Tag,
  Empty,
  Spin,
  Row,
  Col,
  message,
} from 'antd';
import {
  FileTextOutlined,
  ArrowLeftOutlined,
  CarOutlined,
  RightOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import PublicLayout from '../components/landing/PublicLayout';
import { useCustomerAuth } from '../contexts/CustomerAuthContext';
import { customerPortalApi } from '../services/apiInstances';

const { Title, Text } = Typography;

const statusColor = (status?: string): string => {
  switch ((status || '').toLowerCase()) {
    case 'approved':
    case 'completed':
      return 'green';
    case 'declined':
    case 'rejected':
      return 'red';
    case 'sent':
    case 'pending':
      return 'blue';
    default:
      return 'default';
  }
};

const money = (value: any): string => {
  const n = Number(value);
  return `$${(Number.isFinite(n) ? n : 0).toFixed(2)}`;
};

interface EstimateSummary {
  id: number;
  estimate_number: string;
  status: string;
  total_amount: number;
  created_at: string;
  year?: number;
  make?: string;
  model?: string;
}

const CustomerEstimates: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, accessToken } = useCustomerAuth();
  const [loading, setLoading] = useState(true);
  const [estimates, setEstimates] = useState<EstimateSummary[]>([]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/customer/login');
      return;
    }
    const load = async () => {
      setLoading(true);
      try {
        const res = await customerPortalApi.get('/customer-portal/estimates');
        setEstimates(res.data.estimates || []);
      } catch (error) {
        message.error('Could not load your estimates.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [isAuthenticated, navigate, accessToken]);

  if (loading) {
    return (
      <PublicLayout>
        <div style={{ textAlign: 'center', padding: '100px 20px' }}>
          <Spin size="large" />
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(180deg, #f0f2f5 0%, #ffffff 100%)',
        padding: '40px 20px'
      }}>
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
          {/* Header */}
          <Card style={{ marginBottom: 24 }}>
            <Space direction="vertical" size={0}>
              <Button
                type="link"
                icon={<ArrowLeftOutlined />}
                onClick={() => navigate('/customer/dashboard')}
                style={{ padding: 0, marginBottom: 8 }}
              >
                Back to Dashboard
              </Button>
              <Title level={3} style={{ margin: 0 }}>
                <FileTextOutlined /> My Estimates
              </Title>
            </Space>
          </Card>

          {/* List */}
          <Card>
            {estimates.length > 0 ? (
              <List
                dataSource={estimates}
                renderItem={(estimate) => (
                  <List.Item
                    style={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/customer/estimates/${estimate.id}`)}
                  >
                    <List.Item.Meta
                      avatar={<FileTextOutlined style={{ fontSize: 24, color: '#1890ff' }} />}
                      title={
                        <Space wrap>
                          <Text strong>{estimate.estimate_number}</Text>
                          <Tag color={statusColor(estimate.status)}>
                            {(estimate.status || 'unknown').toUpperCase()}
                          </Tag>
                        </Space>
                      }
                      description={
                        <Space direction="vertical" size={0}>
                          <Text type="secondary">
                            <CarOutlined /> {estimate.year} {estimate.make} {estimate.model}
                          </Text>
                          <Text type="secondary">
                            {dayjs(estimate.created_at).format('MMM D, YYYY')}
                          </Text>
                        </Space>
                      }
                    />
                    <Row align="middle" gutter={12}>
                      <Col>
                        <Text strong style={{ fontSize: 16 }}>{money(estimate.total_amount)}</Text>
                      </Col>
                      <Col>
                        <RightOutlined style={{ color: '#bbb' }} />
                      </Col>
                    </Row>
                  </List.Item>
                )}
              />
            ) : (
              <Empty
                description="No estimates yet"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                style={{ padding: '60px 20px' }}
              >
                <Button type="primary" onClick={() => navigate('/book')}>
                  Book a Service
                </Button>
              </Empty>
            )}
          </Card>
        </div>
      </div>
    </PublicLayout>
  );
};

export default CustomerEstimates;
